import * as THREE from 'three';

/**
 * Card Orientation Standard
 *
 * This module defines the standard way to differentiate between landscape and portrait
 * business card triggers and scenes in the AR tracking application.
 *
 * CONVENTIONS:
 *
 * 1. DIMENSIONS:
 *    - Landscape cards: width=1.0, height=0.65 (standard business card aspect ratio)
 *    - Portrait cards: width=0.65, height=1.0 (90° rotation of landscape)
 *
 * 1.5. SCALE:
 *    - Landscape cards: scale=1.0 (no scaling needed)
 *    - Portrait cards: scale=1.5 (uniform scale to compensate for smaller width)
 *    Note: Portrait cards need 1.5x scale to appear the same visual size as landscape
 *
 * 2. NAMING:
 *    - Scene IDs: base name + optional '-portrait' suffix
 *      Examples: 'video-card', 'video-card-portrait'
 *    - Scene names: descriptive name + optional '(Portrait)' suffix
 *      Examples: 'Video Card Overlay', 'Video Card Overlay (Portrait)'
 *    - Trigger IDs: descriptive identifier + orientation
 *      Examples: 'card-vip-landscape', 'card-video-portrait'
 *    - Trigger names: descriptive name with orientation
 *      Examples: 'VIP Card (Landscape)', 'Video Card (Portrait)'
 *
 * 3. ROTATION:
 *    - Landscape portal scenes: Euler(Math.PI / 2, 0, 0)
 *    - Portrait portal scenes: Euler(Math.PI / 2, 0, Math.PI / 2) - adds 90° Z rotation
 *    - Video overlays may need texture rotation depending on video orientation
 *
 * 4. CONFIGURATION:
 *    - Always use CardOrientation enum instead of magic numbers
 *    - Use helper functions to generate consistent configurations
 *    - Track orientation in trigger metadata
 */

/**
 * Card orientation enum
 */
export enum CardOrientation {
  LANDSCAPE = 'landscape',
  PORTRAIT = 'portrait'
}

/**
 * Standard business card aspect ratio (landscape)
 * Based on standard 3.5" × 2" card (1.75:1 ratio)
 */
export const CARD_ASPECT_RATIO = 1.0 / 0.65; // ≈ 1.538

/**
 * Standard scale multipliers for each orientation
 * Portrait cards need 1.5x scale to appear the same visual size as landscape
 */
export const CARD_SCALE_MULTIPLIERS = {
  [CardOrientation.LANDSCAPE]: 1.0,
  [CardOrientation.PORTRAIT]: 1.5
} as const;

/**
 * Standard card dimensions for each orientation
 */
export const CARD_DIMENSIONS = {
  [CardOrientation.LANDSCAPE]: {
    width: 1.0,
    height: 0.65
  },
  [CardOrientation.PORTRAIT]: {
    width: 0.65,
    height: 1.0
  }
} as const;

/**
 * Standard rotation configurations for portal-style scenes
 *
 * IMPORTANT: For MindAR, both orientations use the same rotation!
 * The tracked card is always in the X-Z plane with camera looking from +Y.
 * Portrait vs landscape is determined by the cardWidth/cardHeight dimensions,
 * NOT by rotating the portal differently.
 *
 * The Euler(π/2, 0, 0) rotation makes the portal face "up" toward the camera
 * regardless of whether the card is landscape or portrait.
 */
export const PORTAL_ROTATIONS = {
  [CardOrientation.LANDSCAPE]: new THREE.Euler(Math.PI / 2, 0, 0),
  [CardOrientation.PORTRAIT]: new THREE.Euler(Math.PI / 2, 0, 0)  // Same as landscape!
} as const;

/**
 * Card dimensions interface
 */
export interface CardDimensions {
  width: number;
  height: number;
}

/**
 * Get card dimensions for a given orientation
 */
export function getCardDimensions(orientation: CardOrientation): CardDimensions {
  return { ...CARD_DIMENSIONS[orientation] };
}

/**
 * Get portal rotation for a given orientation
 */
export function getPortalRotation(orientation: CardOrientation): THREE.Euler {
  return PORTAL_ROTATIONS[orientation].clone();
}

/**
 * Get scale multiplier for a given orientation
 */
export function getScaleMultiplier(orientation: CardOrientation): number {
  return CARD_SCALE_MULTIPLIERS[orientation];
}

/**
 * Get scale vector for a given orientation (uniform scaling on all axes)
 */
export function getCardScale(orientation: CardOrientation): THREE.Vector3 {
  const multiplier = getScaleMultiplier(orientation);
  return new THREE.Vector3(multiplier, multiplier, multiplier);
}

/**
 * Detect orientation from dimensions
 */
export function detectOrientation(width: number, height: number): CardOrientation {
  return width >= height ? CardOrientation.LANDSCAPE : CardOrientation.PORTRAIT;
}

/**
 * Naming convention helpers
 */
export const CardNaming = {
  /**
   * Generate a scene ID from a base name and orientation
   * @param baseName Base scene identifier (e.g., 'video-card', 'voodoo-showcase')
   * @param orientation Card orientation
   * @returns Scene ID (e.g., 'video-card', 'video-card-portrait')
   */
  sceneId(baseName: string, orientation: CardOrientation): string {
    return orientation === CardOrientation.PORTRAIT
      ? `${baseName}-portrait`
      : baseName;
  },

  /**
   * Generate a scene name from a base name and orientation
   * @param baseName Descriptive scene name (e.g., 'Video Card Overlay')
   * @param orientation Card orientation
   * @returns Scene name (e.g., 'Video Card Overlay', 'Video Card Overlay (Portrait)')
   */
  sceneName(baseName: string, orientation: CardOrientation): string {
    return orientation === CardOrientation.PORTRAIT
      ? `${baseName} (Portrait)`
      : baseName;
  },

  /**
   * Generate a trigger ID from a base name and orientation
   * @param baseName Base trigger identifier (e.g., 'card-vip', 'card-video')
   * @param orientation Card orientation
   * @returns Trigger ID (e.g., 'card-vip-landscape', 'card-video-portrait')
   */
  triggerId(baseName: string, orientation: CardOrientation): string {
    return `${baseName}-${orientation}`;
  },

  /**
   * Generate a trigger name from a base name and orientation
   * @param baseName Descriptive trigger name (e.g., 'VIP Card', 'Video Card')
   * @param orientation Card orientation
   * @returns Trigger name (e.g., 'VIP Card (Landscape)', 'Video Card (Portrait)')
   */
  triggerName(baseName: string, orientation: CardOrientation): string {
    const orientationLabel = orientation === CardOrientation.LANDSCAPE ? 'Landscape' : 'Portrait';
    return `${baseName} (${orientationLabel})`;
  }
};

/**
 * Scene configuration helper for card-based scenes
 */
export interface CardSceneConfig {
  baseName: string;
  orientation: CardOrientation;
  description?: string;
}

/**
 * Generate scene metadata using standardized naming
 */
export function createCardSceneMetadata(config: CardSceneConfig) {
  return {
    id: CardNaming.sceneId(config.baseName, config.orientation),
    name: CardNaming.sceneName(config.baseName, config.orientation),
    description: config.description || ''
  };
}

/**
 * Trigger configuration helper
 */
export interface CardTriggerConfig {
  baseName: string;
  orientation: CardOrientation;
  targetIndex: number;
  sceneId: string;
  priority: number;
}

/**
 * Generate trigger configuration using standardized naming
 */
export function createCardTriggerConfig(config: CardTriggerConfig) {
  return {
    id: CardNaming.triggerId(config.baseName, config.orientation),
    name: CardNaming.triggerName(config.baseName, config.orientation),
    targetIndex: config.targetIndex,
    sceneId: config.sceneId,
    priority: config.priority,
    // Store orientation in metadata for debugging/tracking
    metadata: {
      orientation: config.orientation
    }
  };
}

/**
 * Quick helpers for common operations
 */
export const CardHelper = {
  /**
   * Check if dimensions match landscape orientation
   */
  isLandscape(width: number, height: number): boolean {
    return detectOrientation(width, height) === CardOrientation.LANDSCAPE;
  },

  /**
   * Check if dimensions match portrait orientation
   */
  isPortrait(width: number, height: number): boolean {
    return detectOrientation(width, height) === CardOrientation.PORTRAIT;
  },

  /**
   * Get the opposite orientation
   */
  flipOrientation(orientation: CardOrientation): CardOrientation {
    return orientation === CardOrientation.LANDSCAPE
      ? CardOrientation.PORTRAIT
      : CardOrientation.LANDSCAPE;
  },

  /**
   * Get human-readable orientation label
   */
  getOrientationLabel(orientation: CardOrientation): string {
    return orientation === CardOrientation.LANDSCAPE ? 'Landscape' : 'Portrait';
  }
};
